/**
 * Sticky "Add to Study" CTA — pinned at the bottom of the chat panel once a
 * pericope commentary has been generated AND a study is open (editing or
 * selected). Writes the commentary onto the study and persists it; the study
 * PDF then prints it as the last page. After a press it confirms generically
 * ("Added" — never the study's full name) and gently fades away.
 */

import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import type { StudyAiCommentary } from '@skjv/common';
import { bibleState } from '@/state/bible-state';
import { CheckIcon } from '@components/icons';

const ADDED_HOLD_MS = 1400;
const FADE_MS = 1200;

type CtaPhase = 'idle' | 'added' | 'fading' | 'gone';

export const AddToStudyCta = observer(() => {
  const [phase, setPhase] = useState<CtaPhase>('idle');
  const [addedFor, setAddedFor] = useState<string | null>(null);

  // The finished pericope commentary lands in its own bubble carrying the
  // source intent. Key on the commentary text, not commentaryState (which
  // flips to 'loaded' before the fetch resolves).
  const commentaryMsg = [...bibleState.chatMessages]
    .reverse()
    .find((m) => m.commentary && m.intent === 'pericope_study');
  const targetStudy = bibleState.editingStudy ?? bibleState.selectedStudy;
  const commentaryText = commentaryMsg?.commentary ?? null;

  // A new commentary resurrects the CTA after a previous add-and-fade.
  useEffect(() => {
    if (addedFor && commentaryText !== addedFor) {
      setAddedFor(null);
      setPhase('idle');
    }
  }, [commentaryText, addedFor]);

  useEffect(() => {
    if (phase === 'added') {
      const t = window.setTimeout(() => setPhase('fading'), ADDED_HOLD_MS);
      return () => window.clearTimeout(t);
    }
    if (phase === 'fading') {
      const t = window.setTimeout(() => setPhase('gone'), FADE_MS);
      return () => window.clearTimeout(t);
    }
  }, [phase]);

  if (!commentaryMsg?.commentary || !commentaryMsg.pericope || !targetStudy) return null;
  if (phase === 'gone') return null;

  const handleAdd = () => {
    const pericope = commentaryMsg.pericope!;
    const aiCommentary: StudyAiCommentary = {
      pericopeTitle: pericope.title,
      reference: `${pericope.book} ${pericope.chapter}:${pericope.startVerse}-${pericope.endVerse}`,
      text: commentaryMsg.commentary!,
      generatedAt: new Date().toISOString(),
    };
    // Seed thoughts entered on the chat card become the study's thoughts;
    // a generation without thoughts leaves the study's existing notes alone.
    const updates = commentaryMsg.readerThoughts
      ? { aiCommentary, readerThoughts: commentaryMsg.readerThoughts }
      : { aiCommentary };

    if (bibleState.editingStudy) {
      bibleState.updateEditingStudy(updates);
      bibleState.saveEditingStudy();
    } else if (bibleState.selectedStudy) {
      bibleState.updateStudy(bibleState.selectedStudy.id, updates);
    }

    setAddedFor(commentaryMsg.commentary!);
    setPhase('added');
    bibleState.showToast('Commentary added — prints as the last page of the study.', 'success');
  };

  return (
    <div className={`add-to-study-footer${phase === 'fading' ? ' add-to-study-footer--fade' : ''}`}>
      <button
        type="button"
        data-testid="add-to-study"
        className="add-to-study-button"
        onClick={handleAdd}
        disabled={phase !== 'idle'}
      >
        {phase !== 'idle' ? (
          <>
            <CheckIcon className="h-4 w-4" /> Added
          </>
        ) : (
          <>Add to Study</>
        )}
      </button>
    </div>
  );
});
